/**
 * Resource Fallback Utility
 * Provides fallback Google search links when article resources are missing or invalid
 */

const DUMMY_PATTERNS = [
  'example.com',
  'example.org',
  'example.net',
  'test.com',
  'placeholder',
  'https://example',
  'http://example',
  'yourlink',
  'insert-link',
  'pending',
];

/**
 * Check if a URL is valid and not a dummy/placeholder
 */
export const isValidArticleUrl = (url: string | undefined): boolean => {
  if (!url || typeof url !== 'string' || url.trim() === '') {
    return false;
  }

  const lowerUrl = url.toLowerCase();

  // Check for dummy patterns
  if (DUMMY_PATTERNS.some((pattern) => lowerUrl.includes(pattern))) {
    return false;
  }

  // Check for valid URL format
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Generate a Google search fallback URL for an article
 * @param articleTitle - The title of the article
 * @param topic - Optional: the topic/section the article is about
 * @param goal - Optional: the learning goal for context
 * @returns Google search URL
 */
export const generateGoogleSearchFallback = (
  articleTitle: string,
  topic?: string,
  goal?: string
): string => {
  let searchTerm = articleTitle;

  // Add context for better search results
  if (goal) {
    searchTerm = `${goal} ${searchTerm}`;
  }
  if (topic) {
    searchTerm = `${searchTerm} ${topic}`;
  }

  // Remove extra whitespace and encode for URL
  searchTerm = searchTerm.trim();
  const encodedTerm = encodeURIComponent(searchTerm);

  return `https://www.google.com/search?q=${encodedTerm}`;
};

/**
 * Get the appropriate article URL - either the real link or a Google search fallback
 */
export const getArticleUrl = (
  articleUrl: string | undefined,
  articleTitle: string,
  topic?: string,
  goal?: string
): { url: string; isFallback: boolean } => {
  if (isValidArticleUrl(articleUrl)) {
    return {
      url: articleUrl!,
      isFallback: false,
    };
  }

  return {
    url: generateGoogleSearchFallback(articleTitle, topic, goal),
    isFallback: true,
  };
};
